import React, { useEffect, useState, Component } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
/**
 * Google Auth Debug Component
 * Use this to test and debug Google OAuth integration
 * Add to any page temporarily: <GoogleAuthDebug />
 */
export function GoogleAuthDebug() {
  const {
    user,
    signInWithGoogle
  } = useAuth();
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  useEffect(() => {
    checkSession();
  }, []);
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };
  const checkSession = async () => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    setSession(session);
    addLog(session ? '✅ Active session found' : '❌ No active session');
  };
  const testGoogleSignIn = async () => {
    setLoading(true);
    addLog('🔐 Initiating Google Sign-In...');
    try {
      await signInWithGoogle();
      addLog('✅ Google Sign-In initiated (redirecting...)');
    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  const testDirectOAuth = async () => {
    setLoading(true);
    addLog('🔐 Testing direct OAuth call...');
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithOAuth({
        provider: 'google'
      });
      if (error) {
        addLog(`❌ OAuth Error: ${error.message}`);
      } else {
        addLog(`✅ OAuth initiated: ${data.url}`);
        addLog('🔄 Redirecting to Google...');
      }
    } catch (error: any) {
      addLog(`❌ Unexpected error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  const checkProviders = async () => {
    addLog('🔍 Checking available providers...');
    try {
      // This will show if Google is enabled in Supabase
      const {
        data,
        error
      } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          skipBrowserRedirect: true
        }
      });
      if (error) {
        addLog(`❌ Provider check failed: ${error.message}`);
      } else {
        addLog('✅ Google provider is available');
      }
    } catch (error: any) {
      addLog(`❌ Error checking providers: ${error.message}`);
    }
  };
  const clearLogs = () => {
    setLogs([]);
  };
  return <div className="fixed bottom-4 right-4 w-96 bg-white rounded-xl shadow-2xl border-2 border-purple-500 p-4 max-h-[600px] overflow-y-auto z-50" data-id="element-559" data-mp="2ihm4he8s0g8g">
      <div className="flex items-center justify-between mb-4" data-id="element-560" data-mp="29iqyo39n985o">
        <h3 className="font-bold text-lg" data-id="element-561" data-mp="29kbx43z2xb8t">🧪 Google Auth Debug</h3>
        <button onClick={clearLogs} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-562" data-mp="2dkm15b3b9rl1">
          Clear Logs
        </button>
      </div>

      {/* Status */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg" data-id="element-563" data-mp="brg040ocwhv8">
        <p className="text-sm font-medium mb-2" data-id="element-564" data-mp="1wai3y91wul8f">Current Status:</p>
        <div className="space-y-1 text-xs" data-id="element-565" data-mp="1warupyus4wf6">
          <p data-id="element-566" data-mp="1s96imfq2km61">
            Session:{' '}
            <span className={session ? 'text-green-600' : 'text-red-600'} data-id="element-567" data-mp="1sahp4nawg80u">
              {session ? '✅ Active' : '❌ None'}
            </span>
          </p>
          <p data-id="element-568" data-mp="219uzrjarf2n5">
            User:{' '}
            <span className={user ? 'text-green-600' : 'text-red-600'} data-id="element-569" data-mp="218tdeioamtq4">
              {user ? `✅ ${user.email}` : '❌ Not logged in'}
            </span>
          </p>
          <p data-id="element-570" data-mp="2azxr7f28g8jd">
            Supabase URL:{' '}
            <span className="text-blue-600 break-all" data-id="element-571" data-mp="2aywi3g4s1w4a">
              {supabase.supabaseUrl}
            </span>
          </p>
        </div>
      </div>

      {/* User Info */}
      {user && <div className="mb-4 p-3 bg-green-50 rounded-lg" data-id="element-572" data-mp="2k0m60bzgacxb">
          <p className="text-sm font-medium mb-2" data-id="element-573" data-mp="ukmpuaxezn86">User Info:</p>
          <div className="space-y-1 text-xs" data-id="element-574" data-mp="un8zcg3v8ct1">
            <p data-id="element-575" data-mp="pmv11ps2gh87">Email: {user.email}</p>
            <p data-id="element-576" data-mp="pnwbpt5671pe">Name: {user.user_metadata?.fullName || 'Not set'}</p>
            <p data-id="element-577" data-mp="rzor2un1232e">Provider: {user.app_metadata?.provider || 'Unknown'}</p>
          </div>
        </div>}

      {/* Test Buttons */}
      <div className="space-y-2 mb-4" data-id="element-578" data-mp="w0igxtk15rxx">
        <button onClick={testGoogleSignIn} disabled={loading} className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50" data-id="element-579" data-mp="111oblwx93pa3">
          {loading ? 'Testing...' : 'Test Google Sign-In (Context)'}
        </button>

        <button onClick={testDirectOAuth} disabled={loading} className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50" data-id="element-580" data-mp="db2doygynldt">
          Test Direct OAuth Call
        </button>

        <button onClick={checkProviders} disabled={loading} className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50" data-id="element-581" data-mp="3lrndbonpd249">
          Check Google Provider Status
        </button>

        <button onClick={checkSession} className="w-full bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700" data-id="element-582" data-mp="3j54sz88zb9cr">
          Refresh Session Status
        </button>
      </div>

      {/* Logs */}
      <div className="bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-xs max-h-48 overflow-y-auto" data-id="element-583" data-mp="348wztiflexlc">
        {logs.length === 0 ? <p className="text-gray-500" data-id="element-584" data-mp="30ag55ydczfkt">No logs yet. Click a button to test.</p> : logs.map((log, index) => <p key={index} data-id="element-585" data-mp="2qijsniaizmx2" data-mpi={index}>{log}</p>)}
      </div>

      {/* Quick Links */}
      <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-586" data-mp="2qgft1cjnta2l">
        <p className="text-xs text-gray-600 mb-2" data-id="element-587" data-mp="2lki9kxd3nnll">Quick Links:</p>
        <div className="space-y-1" data-id="element-588" data-mp="2gk4aw5xe1q08">
          <a href="https://supabase.com/dashboard/project/neadfoxscgjeekvabnoz/auth/providers" target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 hover:underline" data-id="element-589" data-mp="2gj2ox6eu3j3q">
            → Supabase Auth Providers
          </a>
          <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 hover:underline" data-id="element-590" data-mp="26xjnzj0xbnes">
            → Google Cloud Console
          </a>
        </div>
      </div>
    </div>;
}